import React from 'react';
import { List, Pin } from 'lucide-react';
import { useSidebar } from './ui/sidebar';
import {
  FLOATING_EDITOR_CONTROL_MOBILE_LEFT,
  floatingEditorControlTop,
} from '../constants/layout';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';

interface FloatingTocButtonProps {
  tocHeadings: Array<{ level: number; text: string; pos: number; kind?: 'heading' | 'scene' }>;
  isFloatingTocVisible: boolean;
  setIsFloatingTocVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isTocPinned: boolean;
  setIsTocPinned: React.Dispatch<React.SetStateAction<boolean>>;
  isMobile: boolean;
  isFocusMode: boolean;
  hasMainAreaView: boolean;
  t: (key: string) => string;
}

export const FloatingTocButton: React.FC<FloatingTocButtonProps> = React.memo(({
  isFloatingTocVisible,
  setIsFloatingTocVisible,
  isTocPinned,
  setIsTocPinned,
  isMobile,
  isFocusMode,
  hasMainAreaView,
  t,
}) => {
  let sidebar;
  try {
    sidebar = useSidebar();
  } catch {
    sidebar = null;
  }
  const leftOffset = isMobile ? FLOATING_EDITOR_CONTROL_MOBILE_LEFT : (isFocusMode ? '1rem' : (sidebar?.open ? '17rem' : '4rem'));
  const topPosition = floatingEditorControlTop(isFocusMode);
  const showPinAction = !isMobile && isFloatingTocVisible && !isTocPinned;
  const isHidden = (isMobile && isFloatingTocVisible) || isTocPinned;

  if (hasMainAreaView) {
    return null;
  }

  const ariaLabel = showPinAction
    ? t('pin_table_of_contents')
    : t('table_of_contents');

  const tooltip = showPinAction
    ? t('pin_table_of_contents_tooltip')
    : undefined;

  return (
    <TooltipProvider delayDuration={0}>
      <ButtonTooltip tooltip={tooltip} side="top" hidden={isMobile}>
        <button
          type="button"
          data-floating-toc-button="true"
          className="floating-editor-control"
          onMouseEnter={() => {
            if (!isMobile && !isTocPinned) {
              setIsFloatingTocVisible(true);
            }
          }}
          onClick={() => {
            if (showPinAction) {
              setIsTocPinned(true);
              setIsFloatingTocVisible(false);
              return;
            }
            setIsFloatingTocVisible((prev) => !prev);
          }}
          style={{
            position: 'fixed',
            top: topPosition,
            left: leftOffset,
            zIndex: showPinAction ? 41 : 5,
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            border: 'none',
            padding: 0,
            color: 'var(--muted-foreground)',
            opacity: isHidden ? 0 : 1,
            transition: 'opacity 0.2s, background-color 0.2s, box-shadow 0.2s',
            pointerEvents: isHidden ? 'none' : 'auto',
          }}
          aria-label={ariaLabel}
        >
          {showPinAction ? (
            <Pin className="h-5 w-5" style={{ color: 'currentColor' }} />
          ) : (
            <List className="h-5 w-5" style={{ color: 'currentColor' }} />
          )}
        </button>
      </ButtonTooltip>
    </TooltipProvider>
  );
});

FloatingTocButton.displayName = 'FloatingTocButton';
